// Generated by TaroBase SDK Generator: 2026-04-14T11:42:05.668Z
// Collection: wipLikes/$likeId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Likes on WIP art posts. One like per wallet per WIP post, enforced by frontend using composite IDs. Public read, authenticated create, owner can unlike (delete).
 */

export interface WipLikesRequest {
  postId: string;
  walletAddress: string;
  createdAt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface WipLikesResponse {
  postId: string;
  walletAddress: string;
  createdAt: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a WipLikes operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildWipLikes(likeId: string, data?: WipLikesRequest): DocumentOperation {
  const doc = data ? transformValues(data) : {};
  return { path: `wipLikes/${likeId}`, document: doc };
}

/**
 * Authenticated users only. The walletAddress field must match the caller's wallet address. postId, walletAddress, and createdAt are immutable after creation. Frontend should use a composite ID like '{walletAddress}_{postId}' (using underscore, NOT hyphens) to enforce one like per wallet per post. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setWipLikes(likeId: string, data?: WipLikesRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data || {});
    const succeeded = await set(`wipLikes/${likeId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting WipLikes: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read likes. Used to display like counts and whether the current user has liked a WIP post.
   (Get Single Item)
 */
export async function getWipLikes(likeId: string): Promise<WipLikesResponse | null> {
  try {
    const result = await get(`wipLikes/${likeId}`);
    return result as WipLikesResponse | null;
  } catch (error) {
    console.error(`Error getting WipLikes: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single WipLikes document. (
  Read Operation Details: Anyone can read likes. Used to display like counts and whether the current user has liked a WIP post.
  )
 */
export function subscribeWipLikes(callback: (data: WipLikesResponse | null) => void, likeId: string): Promise<() => Promise<void>> {
  return subscribe(`wipLikes/${likeId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as WipLikesResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to WipLikes at path '${`wipLikes/${likeId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many WipLikes items from collection wipLikes
 
  Read Operation Details: Anyone can read likes. Used to display like counts and whether the current user has liked a WIP post.
  
 */
export async function getManyWipLikes(filter: string = ""): Promise<WipLikesResponse[]> {
  try {
    const results = await get("wipLikes", { prompt: filter });
    return Array.isArray(results) ? results as WipLikesResponse[] : [];
  } catch (error) {
    console.error(`Error getting WipLikes collection at path 'wipLikes': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in WipLikes collection at wipLikes
 
  Read Operation Details: Anyone can read likes. Used to display like counts and whether the current user has liked a WIP post.
  
 */
export function subscribeManyWipLikes(callback: (data: WipLikesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("wipLikes", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as WipLikesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to WipLikes collection at path '${"wipLikes"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all WipLikes items from collection wipLikes
 
  Read Operation Details: Anyone can read likes. Used to display like counts and whether the current user has liked a WIP post.
  
 */
export async function getAllWipLikes(filter: string = ""): Promise<WipLikesResponse[]> {
  try {
    const results = await get("wipLikes", { prompt: filter });
    return Array.isArray(results) ? results as WipLikesResponse[] : [];
  } catch (error) {
    console.error(`Error getting WipLikes collection at path 'wipLikes': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in WipLikes collection at wipLikes
 
  Read Operation Details: Anyone can read likes. Used to display like counts and whether the current user has liked a WIP post.
  
 */
export function subscribeAllWipLikes(callback: (data: WipLikesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("wipLikes", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as WipLikesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to WipLikes collection at path '${"wipLikes"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count WipLikes items in collection wipLikes.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countWipLikes(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("wipLikes", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting WipLikes at path 'wipLikes': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on WipLikes items in collection wipLikes.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateWipLikes(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("wipLikes", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating WipLikes at path 'wipLikes': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * 
  Delete Operation Details: Only the liker (matching walletAddress) can remove their like to unlike a post.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteWipLikes(likeId: string): Promise<boolean> {
  try {
    const succeeded = await set(`wipLikes/${likeId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting WipLikes: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for WipLikes for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteWipLikes(likeId: string): DocumentOperation {
  return { path: `wipLikes/${likeId}`, document: null };
}

